import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useAuthContext } from 'contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { httpGetUsers } from 'services/users';
import { ReplaceSavingProvider, useReplaceSaving } from 'contexts/ReplaceSavingContext';
import DashboardHeader from './DashboardHeader';
import PumpsMainView from './PumpsMainView';
import PoolsMainView from './PoolsMainView';
import ReplacePumpInPoolView from './ReplacePumpInPoolView';
import UsersMainView from './UsersMainView';
import DashboardSidebar from './DashboardSidebar';
import DashboardNavMobile from './DashboardNavMobile';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import ReportPage from 'views/dashboard/ReportPage';
import './Dashboard.scss';

const POOL_STORAGE_KEY = 'pumpsListSelectedPool';

function DashboardLayoutInner() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { replaceSavingInProgress, setReplaceSavingInProgress } = useReplaceSaving();
  const replaceSavingInState = location.state?.replaceSaving === true;
  const showReplaceSavingOverlay = replaceSavingInProgress || replaceSavingInState;

  // When navigating to a new page with replaceSaving state, reset the context
  useEffect(() => {
    if (replaceSavingInState) {
      setReplaceSavingInProgress(false);
    }
  }, [replaceSavingInState, setReplaceSavingInProgress]);

  const [selectedPool, setSelectedPool] = useState(() => {
    const stored = sessionStorage.getItem(POOL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const { pumps, loading } = useStoreState((state) => state.pumps);
  const { getPumps } = useStoreActions((actions) => actions.pumps);
  const { userRole } = useAuthContext();

  // Debug logging
  console.log('DashboardLayout render');
  console.log('DashboardLayout: pumps state:', pumps);
  console.log('DashboardLayout: loading state:', loading);
  console.log('DashboardLayout: pumps type:', typeof pumps);
  console.log('DashboardLayout: pumps is array:', Array.isArray(pumps));
  console.log('DashboardLayout: selectedPool:', selectedPool);

  // Load full pump list once per mount; pool filter is client-side (PumpsMainView) so WebSocket
  // updates keep working without replacing the whole list on every pool change.
  useEffect(() => {
    console.log('DashboardLayout: Fetching pumps (full list for client-side filters)');
    getPumps()
      .catch((error) => {
        console.error('Error fetching pumps:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fetch on mount only; pool is UI filter
  }, []);

  // Fetch users for UsersMainView (re-fetch when role is known so client filter matches API)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const response = await httpGetUsers({ limit: 2000, offset: 0 });
        const usersData = Array.isArray(response.data) ? response.data : response.data.results || [];
        let list = usersData;
        if (Number(userRole) === 2) {
          list = usersData.filter((u) => Number(u.role) !== 1);
        } else if (Number(userRole) === 4) {
          // Mine user: mine users and maintenance users only
          list = usersData.filter((u) => [4, 5].includes(Number(u.role)));
        } else if (Number(userRole) === 5) {
          list = usersData.filter((u) => Number(u.role) === 5);
        } else if (Number(userRole) === 3) {
          list = usersData.filter((u) => Number(u.role) === 3);
        }
        setUsers(list);
        setUsersError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setUsersError(t('users_fetch_error'));
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [t, userRole]);

  const handlePoolSelect = (pool) => {
    console.log('DashboardLayout: Pool selected:', pool);
    setSelectedPool(pool);
    if (pool) {
      sessionStorage.setItem(POOL_STORAGE_KEY, JSON.stringify(pool));
    } else {
      sessionStorage.removeItem(POOL_STORAGE_KEY);
    }
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
  };

  const handleUserAdded = (newUser) => {
    setUsers(prev => [...prev, newUser]);
  };

  const handleUserDeleted = (deletedId) => {
    setUsers((prev) => prev.filter((u) => u.id !== deletedId));
    navigate('/dashboard/users');
  };

  const transformedPumps = useMemo(() => {
    const pumpsArray = Array.isArray(pumps) ? pumps : [];
    const locale = i18n.language === 'en' ? 'en-GB' : 'fi-FI';
    const formatLastService = (lastMaintenanceDate, formattedActiveTime) => {
      if (formattedActiveTime) return formattedActiveTime;
      if (!lastMaintenanceDate) return t('pump_no_maintenance');
      const date = new Date(lastMaintenanceDate);
      return t('pump_last_maintenance', { date: date.toLocaleDateString(locale) });
    };
    return pumpsArray.map((pump) => ({
      id: pump.id,
      name: pump.name,
      currentPoolId: pump.current_pool?.id ?? null,
      pool: pump.current_pool ? pump.current_pool.name : t('pump_no_pool'),
      lastService: formatLastService(pump.last_maintenance_date, pump.formatted_active_time),
      statusCode: pump.status,
      serialNumber: pump.serial_number,
      model: pump.model,
      description: pump.description,
      notes: pump.notes,
      unacknowledged_comments_count: pump.unacknowledged_comments_count || 0,
      usageTimeLimitExceeded: Boolean(pump.usage_time_limit_exceeded),
      usageTimeWarning: Boolean(pump.usage_time_warning),
      formattedActiveTime: pump.formatted_active_time || null,
    }));
  }, [pumps, t, i18n.language]);

  console.log('DashboardLayout: Pumps from API:', pumps);
  console.log('DashboardLayout: Transformed pumps:', transformedPumps);
  console.log('DashboardLayout: Current user role:', userRole);

  // Role-based route protection
  const ProtectedRoute = ({ children, allowedRoles }) => {
    console.log('DashboardLayout: ProtectedRoute - User role:', userRole);
    console.log('DashboardLayout: ProtectedRoute - Allowed roles:', allowedRoles);
    if (userRole && !allowedRoles.includes(userRole)) {
      console.log('DashboardLayout: ProtectedRoute - Access denied, redirecting to dashboard');
      return <Navigate to="/dashboard" replace />;
    }
    console.log('DashboardLayout: ProtectedRoute - Access granted');
    return children;
  };

  return (
    <div className="dashboard-layout">
      {showReplaceSavingOverlay && (
        <div className="dashboard-saving-overlay" aria-live="polite" aria-busy="true">
          <div className="dashboard-saving-spinner-wrap">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('dashboard_saving_spinner')}</span>
            </div>
            <p className="dashboard-saving-label">{t('dashboard_saving_label')}</p>
          </div>
        </div>
      )}
      <DashboardHeader />
      <div className="dashboard-content-flex">
        <DashboardSidebar />
        <div className="dashboard-main-wrapper" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="reports" element={
              <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
                <ReportPage />
              </ProtectedRoute>
            } />
            <Route path="users/:userId" element={
              <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
                <UsersMainView 
                  users={users}
                  loading={usersLoading}
                  error={usersError}
                  onUserUpdated={handleUserUpdated}
                  onUserAdded={handleUserAdded}
                  onUserDeleted={handleUserDeleted}
                />
              </ProtectedRoute>
            } />
            <Route path="users" element={
              <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
                <UsersMainView 
                  users={users}
                  loading={usersLoading}
                  error={usersError}
                  onUserUpdated={handleUserUpdated}
                  onUserAdded={handleUserAdded}
                  onUserDeleted={handleUserDeleted}
                />
              </ProtectedRoute>
            } />
            <Route path="pools/:poolId/replace-pump/:currentPumpId" element={
              <ProtectedRoute allowedRoles={[1, 2, 4]}>
                <ReplacePumpInPoolView />
              </ProtectedRoute>
            } />
            <Route path="pools/:poolId" element={
              <ProtectedRoute allowedRoles={[1, 2, 4, 5]}>
                <PoolsMainView />
              </ProtectedRoute>
            } />
            <Route path="pools" element={
              <ProtectedRoute allowedRoles={[1, 2, 4, 5]}>
                <PoolsMainView />
              </ProtectedRoute>
            } />
            <Route path="pumps/:pumpId/events" element={
              <PumpsMainView 
                pumps={transformedPumps}
                loading={loading}
                selectedPool={selectedPool}
                onPoolSelect={handlePoolSelect}
              />
            } />
            <Route path="pumps/:pumpId" element={
              <PumpsMainView 
                pumps={transformedPumps}
                loading={loading}
                selectedPool={selectedPool}
                onPoolSelect={handlePoolSelect}
              />
            } />
            <Route path="pumps" element={
              <PumpsMainView 
                pumps={transformedPumps}
                loading={loading}
                selectedPool={selectedPool}
                onPoolSelect={handlePoolSelect}
              />
            } />
            <Route path="*" element={
              <PumpsMainView 
                pumps={transformedPumps}
                loading={loading}
                selectedPool={selectedPool}
                onPoolSelect={handlePoolSelect}
              />
            } />
          </Routes>
        </div>
      </div>
      <DashboardNavMobile />
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <ReplaceSavingProvider>
      <DashboardLayoutInner />
    </ReplaceSavingProvider>
  );
} 